import { Box, Stack, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { Account } from '@/api/accounts';
import { HeadedCard } from '@/components/HeadedCard';
import { MoneyText } from '@/components/MoneyText';
import { formatCivilDate, formatInstant, formatMoneyMinor } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { useMe } from '@/session/meHooks';
import { resolveTokens } from '@/theme/theme';

interface BankLedgerTileProps {
  account: Account;
}

function formatAsOf(value: string, locale: string, timeZone: string): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    try {
      return formatCivilDate(value, locale);
    } catch {
      return value;
    }
  }
  try {
    return formatInstant(value, locale, timeZone);
  } catch {
    return value;
  }
}

/**
 * Bank says / ledger says (TMMAPP-0024 §6). Both figures are served; the difference is
 * computed for display only — this is not a ledger computation.
 */
export function BankLedgerTile({ account }: BankLedgerTileProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const me = useMe();
  const tokens = resolveTokens(useTheme());
  const connector = account.connector;
  if (connector === null || connector.bankBalanceMinor === null) return null;
  const currency = account.currency ?? account.operatingCurrency ?? me.user.currency ?? '';
  const ledger = account.balances?.bankBalanceMinor ?? null;
  const difference = ledger !== null ? connector.bankBalanceMinor - ledger : null;
  const asOf =
    connector.balanceAsOf !== null
      ? formatAsOf(connector.balanceAsOf, locale, me.user.timezone ?? 'UTC')
      : null;

  return (
    <HeadedCard title={t('connections.bankLedgerTitle')}>
      <Stack direction={{ xs: 'column', md: 'row' }} sx={{ gap: 3, flexWrap: 'wrap' }}>
        <Box>
          <Typography
            variant="kindLabel"
            component="div"
            sx={{ color: tokens.textLabel('surface') }}
          >
            {t('connections.bankSays')}
          </Typography>
          <MoneyText
            minor={connector.bankBalanceMinor}
            currency={currency}
            variant="figureSm"
            component="div"
          />
          {asOf !== null && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {t('connections.asOf', { balanceAsOf: asOf })}
            </Typography>
          )}
        </Box>
        <Box>
          <Typography
            variant="kindLabel"
            component="div"
            sx={{ color: tokens.textLabel('surface') }}
          >
            {t('connections.ledgerSays')}
          </Typography>
          <MoneyText minor={ledger} currency={currency} variant="figureSm" component="div" />
        </Box>
      </Stack>
      {difference !== null && (
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 1 }}>
          {t('connections.difference', {
            amount: formatMoneyMinor(difference, currency, locale),
          })}
        </Typography>
      )}
    </HeadedCard>
  );
}
